import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X } from "lucide-react";

interface CameraCaptureProps {
  onCapture: (canvas: HTMLCanvasElement) => void;
  onClose: () => void;
}

export function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setStarted(true);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Camera access denied. Please allow camera permission.",
          );
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    // Draw current video frame to an offscreen canvas
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 1280;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0, w, h);

    // Stop camera immediately — frame is captured, device no longer needed
    stopStream();

    onCapture(canvas);
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-safe pt-4 pb-2">
        <span className="text-white/70 text-sm font-medium">Take a photo</span>
        <button
          type="button"
          onClick={handleClose}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Viewfinder */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center">
        {error ? (
          <div className="flex flex-col items-center gap-3 px-6 text-center">
            <Camera className="w-12 h-12 text-white/30" />
            <p className="text-white/70 text-sm">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
        )}

        {/* 1:1 crop guide overlay */}
        {started && !error && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div
              className="border-2 border-white/40 rounded-2xl"
              style={{ width: "min(80vw, 80vh)", height: "min(80vw, 80vh)" }}
            >
              {/* Corner marks */}
              {(["tl", "tr", "bl", "br"] as const).map((pos) => (
                <div
                  key={pos}
                  className={`absolute w-5 h-5 border-white border-2 ${
                    pos === "tl" ? "top-0 left-0 border-r-0 border-b-0 rounded-tl-lg" :
                    pos === "tr" ? "top-0 right-0 border-l-0 border-b-0 rounded-tr-lg" :
                    pos === "bl" ? "bottom-0 left-0 border-r-0 border-t-0 rounded-bl-lg" :
                              "bottom-0 right-0 border-l-0 border-t-0 rounded-br-lg"
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Capture button */}
      <div className="flex items-center justify-center pb-safe pb-8 pt-4">
        {started && !error && (
          <button
            type="button"
            onClick={handleCapture}
            className="w-16 h-16 rounded-full bg-white active:scale-95 transition-transform shadow-lg ring-4 ring-white/30 flex items-center justify-center"
          >
            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-300" />
          </button>
        )}
      </div>
    </div>
  );
}
